import { parseAsString, useQueryStates } from 'nuqs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCartDrawer } from '@sola/ark-skippy/contexts/cart-drawer.tsx';
import { useCart } from '@sola/cart-sdk/store.tsx';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { ChildGiftLimitItem } from '@sola/schema/dotcom/proxy-api/giftLimits.ts';
import { type GraphQLCommitment, GraphQLCommitmentType, type GraphQLSupporterGiftLimits } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useContentSettings } from '@sola/sdg-sdk/hooks.ts';
import { useReadyForQueries, useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { commitmentsQuery, giftLimitsQuery } from '@sola/supporter-sdk/queries.ts';
import { isCommitmentActive, isCommitmentInSponsorshipTransition } from '@sola/supporter-sdk/utils/commitments.ts';
import type { GiftOption } from '../../types/gifts.ts';
import { formatGiftOptions } from '../../utils/gift-availability/gift-availability.ts';
import { buildGiftCartItemPayload } from '../../utils/gift-cart/gift-cart-payload.ts';
import type { GiftOptionsSubmitData } from '../gift-options-dialog/gift-options-dialog.tsx';
export const getCardElementKey = (participantId: string, giftType: string) => `${giftType.toLowerCase()}-${participantId}`;
const parseGiftType = (giftTypeString: string | null): GiftTypeEnum | null => {
  if (!giftTypeString) {
    return null;
  }
  return Object.values(GiftTypeEnum).find(value => value.toLowerCase() === giftTypeString.toLowerCase()) ?? null;
};
export interface SelectedGiftOptions {
  commitment: GraphQLCommitment;
  giftOption: GiftOption;
  giftType: GiftTypeEnum;
}
export interface RemovedGift {
  cartItemId: string;
  childName: string;
  giftType: GiftTypeEnum;
}
const getSelectedGiftTypeLimits = (giftLimits: GraphQLSupporterGiftLimits | undefined, selectedGiftOptions: SelectedGiftOptions | null): ChildGiftLimitItem | undefined => {
  if (!giftLimits || !selectedGiftOptions) {
    return undefined;
  }
  const {
    commitment,
    giftOption,
    giftType
  } = selectedGiftOptions;
  const childGlobalId = commitment.participant?.globalId ?? commitment.globalId;
  const giftLimitGroup = giftType === GiftTypeEnum.Center ? giftLimits.projects.find(project => project.id === giftOption.centerId) : giftLimits.children.find(child => child.id === childGlobalId);
  return giftLimitGroup?.giftTypes[giftType] ??
  // TODO: Is it wise to ever return the defaults for a Final Gift?
  giftLimits.defaults[giftType] ?? undefined;
};
export const useChildGiftsLogic = (
// TODO: Is childCenterMap ever populated?
childCenterMap: Record<string, string> = {}) => {
  const {
    t
  } = useTranslation('my');
  const [{
    id: activeChildId,
    type: activeGiftType
  }, setActiveQuery] = useQueryStates({
    id: parseAsString,
    type: parseAsString
  });
  const {
    isLoading: isContentSettingsLoading
  } = useContentSettings();
  const {
    globalId
  } = useReadyForQueries();
  const {
    actions: {
      addItems,
      removeItem,
      updateItem
    }
  } = useCart('actions');
  const {
    cart
  } = useCart('cart');
  const {
    data: [giftLimits, commitments],
    isLoading,
    isPending,
    isError
  } = useSupporterQuery([giftLimitsQuery, commitmentsQuery]);
  const giftCommitments = commitments?.filter(c => !isCommitmentInSponsorshipTransition(c));
  const isGiftListLoading = !isError && (!giftLimits || !giftCommitments) && (isPending || isLoading);
  // Basing hasCommitments on unfiltered commitments (to not redirect to /sponsorships/new)
  const hasCommitments = !!commitments?.length;
  const childGiftOptions = useMemo(() => formatGiftOptions(giftLimits, giftCommitments, cart?.items ?? [], childCenterMap), [giftLimits, giftCommitments, cart?.items, childCenterMap]);
  const shouldShowFinalGiftSection = isGiftListLoading || childGiftOptions.Final.length > 0;
  const shouldShowOtherGiftSections = useMemo(() => {
    if (isGiftListLoading) {
      return true;
    }
    const activeChildSponsorshipCount = (giftCommitments ?? []).filter(commitment => isCommitmentActive(commitment) && commitment.commitmentType === GraphQLCommitmentType.ChildSponsorship && !!commitment.participant?.globalId).length;
    const activeFinalCount = childGiftOptions.Final.filter(({
      commitment: commitment_0
    }) => isCommitmentActive(commitment_0)).length;

    // The "Final-only" suppression only applies when every active sponsorship
    // is final. Closed commitments don't count because those sponsorships have
    // already ended, so other sections remain relevant for active children.
    return activeFinalCount !== activeChildSponsorshipCount;
  }, [giftCommitments, childGiftOptions.Final.length, isGiftListLoading]);
  const [selectedGiftOptions, setSelectedGiftOptions] = useState<SelectedGiftOptions | null>(null);
  const {
    openCartDrawer,
    clearCartDrawerAlert
  } = useCartDrawer();
  const querySelection = useMemo<SelectedGiftOptions | null>(() => {
    if (!activeChildId || !activeGiftType || isLoading) {
      return null;
    }
    const giftType = parseGiftType(activeGiftType);
    if (!giftType) {
      return null;
    }
    const list = childGiftOptions?.[giftType] ?? [];
    const match = list.find(({
      commitment: commitment_1
    }) => commitment_1.participant?.globalId === activeChildId);
    return match ? {
      commitment: match.commitment,
      giftOption: match.giftOption,
      giftType
    } : null;
  }, [activeChildId, activeGiftType, childGiftOptions, isLoading]);

  // Drive everything off whichever selection is current; manual click takes
  // precedence so closing the dialog doesn't get re-opened from the URL.
  const activeSelection = selectedGiftOptions ?? querySelection;
  const selectedGiftTypeLimits = getSelectedGiftTypeLimits(giftLimits, activeSelection);
  const handleGiftOptionsOpen = (selection: SelectedGiftOptions) => {
    setSelectedGiftOptions(selection);
    setActiveQuery({
      id: selection.commitment.participant?.globalId ?? null,
      type: selection.giftType.toLowerCase()
    });
  };
  const handleGiftOptionsSubmit = async ({
    amount = 0,
    frequency
  }: GiftOptionsSubmitData) => {
    clearCartDrawerAlert();
    if (!activeSelection) {
      throw new Error('Unable to add gift without selected gift options');
    }
    const {
      commitment: commitment_2,
      giftOption,
      giftType: giftType_0
    } = activeSelection;

    /** NOTE: if we're editing an existing item, return early */
    const cartItemId = giftOption.cartItem?.cartId;
    const isExistingItem = !!cartItemId;
    if (isExistingItem) {
      const updateErrors = await updateItem(cartItemId, {
        unitPrice: amount,
        commitmentFrequency: frequency
      });
      if (updateErrors?.length) {
        throw new Error('Unable to update gift in cart');
      }
      openCartDrawer();
      return;
    }
    const giftCartItem = buildGiftCartItemPayload({
      amount,
      centerId: giftOption.centerId,
      commitment: commitment_2,
      frequency,
      giftType: giftType_0
    });
    const addItemErrors = await addItems({
      items: giftCartItem,
      supporterId: globalId
    });
    if (addItemErrors?.length) {
      throw new Error('Unable to add gift to cart');
    }
    openCartDrawer();
  };
  const handleGiftRemove = async ({
    cartItemId: cartItemId_0,
    childName,
    giftType: giftType_1
  }: RemovedGift) => {
    const removeItemErrors = await removeItem(cartItemId_0);
    if (removeItemErrors?.length) {
      throw new Error('Unable to remove gift from cart');
    }
    openCartDrawer({
      alert: {
        variant: 'positive',
        message: t('my:gifts.removeGiftSuccess.message', {
          childName,
          giftType: t(`my:gifts.typeLabels.${giftType_1.toLowerCase()}`)
        })
      }
    });
  };
  const handleGiftOptionsOpenChange = (open: boolean) => {
    if (open) {
      return;
    }
    setSelectedGiftOptions(null);
    setActiveQuery({
      id: null,
      type: null
    });
  };
  const lastScrolledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!querySelection || selectedGiftOptions || !activeChildId) {
      return;
    }
    const key = getCardElementKey(activeChildId, querySelection.giftType);
    if (lastScrolledKeyRef.current === key) {
      return;
    }
    const activeCard = document.querySelector<HTMLElement>(`#${CSS.escape(key)}`);
    if (!activeCard) {
      return;
    }
    lastScrolledKeyRef.current = key;
    activeCard.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }, [querySelection, selectedGiftOptions, activeChildId]);
  return {
    isLoading,
    isGiftListLoading,
    isError,
    isContentSettingsLoading,
    hasCommitments,
    giftLimits,
    childGiftOptions,
    shouldShowFinalGiftSection,
    shouldShowOtherGiftSections,
    activeSelection,
    selectedGiftTypeLimits,
    handleGiftOptionsOpen,
    handleGiftOptionsSubmit,
    handleGiftRemove,
    handleGiftOptionsOpenChange
  };
};